import React, { useContext, useEffect, useRef, useState } from 'react';
import { BsThreeDots } from 'react-icons/bs';
import { MdOutlineModeEdit } from 'react-icons/md';
import { RiDeleteBinLine } from 'react-icons/ri';
import PLUS_ICON from '../assets/icons/ComniqaiIcon-V3.0/plus-circle-icon.svg';
import STAR_ICON from '../assets/icons/ComniqaiIcon-V3.0/white-star-icon.svg';
import { deleteWithToken, getLocalStorage, getWithToken, patchWithToken, postWithToken } from '../API/Api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomPopUp from '../components/CustomPopUp';
import { ProgressContext } from '../contexts/ProgressContext';

const FAQManager = () => {
  const Navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [apiTokenData, setApiTokenData] = useState('');
  const [loader, setLoader] = useState(false);
  const [faqList, setFaqList] = useState([]);
  const [countList, setCountList] = useState([]);
  const [addFaqInput, setAddFaqInput] = useState(false);
  const [faqFormData, setFaqFormData] = useState({
    questionText: '',
    answerText: '',
  });
  const [deleteAllFAQ, setDeleteAllFAQ] = useState(false);
  const { isProgressActive, urlStats } = useContext(ProgressContext);
  const prevIsProgressActive = useRef(isProgressActive);

  const [deleteFAQ, setDeleteFAQ] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);

  useEffect(() => {
    let token = getLocalStorage('apiToken');
    if (!token) {
      Navigate('/login');
      return;
    }
    const parsedToken = JSON.parse(token);
    setApiTokenData(parsedToken);

    fetchFAQList(parsedToken);
  }, []);

  useEffect(() => {
    let interval;
    const token = getLocalStorage('apiToken');
    if (!token) {
      Navigate('/login');
      return;
    }

    const parsedToken = JSON.parse(token);
    setApiTokenData(parsedToken);

    // 🔹 First-time fetch
    if (!urlStats?.is_faq_generated) {
      fetchFAQList(parsedToken);
    }

    // 🔹 Polling when active
    if (isProgressActive && !urlStats?.is_faq_generated) {
      interval = setInterval(() => {
        fetchFAQList(parsedToken);
      }, 10000);
    }

    // 🔹 One-time fetch when isProgressActive goes from true → false
    if (prevIsProgressActive.current && !isProgressActive) {
      fetchFAQList(parsedToken);
    }

    // Update previous value
    prevIsProgressActive.current = isProgressActive;

    return () => clearInterval(interval);
  }, [isProgressActive, Navigate, urlStats]);

  const fetchFAQList = (token) => {
    setLoader(true);
    getWithToken('Questions_API/', token)
      .then((response) => {
        if (response.status === 200) {
          let formattedFaqList = [];
          response.data.forEach((element, index) => {
            formattedFaqList.push({
              value: false,
              index: index,
              question: element.questions,
              answer: element.answers,
              id: element.id,
              source: element.source,
              source_name: element.source_name,
            });
          });
          setFaqList(formattedFaqList);
          setCountList(response?.count);
        }
      })
      .catch((error) => {
        toast.error('Something went wrong');
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleFaqInputChange = (e) => {
    const { name, value } = e.target;
    setFaqFormData({
      ...faqFormData,
      [name]: value,
    });
  };

  const handleSaveFaq = () => {
    const payload = {
      questions: faqFormData.questionText,
      answers: faqFormData.answerText,
    };
    postWithToken('Questions_ALL_API/', apiTokenData, payload)
      .then((response) => {
        if (response.status === 200) {
          toast.success(response.message);
          fetchFAQList(apiTokenData);
          setAddFaqInput(false);
          setFaqFormData({ questionText: '', answerText: '' });
        } else {
          toast.error(response.message || 'Something went wrong.');
          setAddFaqInput(false);
        }
      })
      .catch((error) => {
        toast.error(error?.message || 'An unexpected error occurred.');
      });
  };

  const handleEditSaveFaq = (idx) => {
    const newData = faqList[idx];
    const obj = {
      questions: newData.question,
      answers: newData.answer,
    };

    patchWithToken('Questions_API/' + newData.id, apiTokenData, obj)
      .then((response) => {
        if (response.status === 200) {
          toast.dismiss();
          toast.success(response.message || 'Message successfully updated.');
          fetchFAQList(apiTokenData);
          setIsEditing(false);
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error('error: ', error);
        toast.error('Something went wrong');
      });
  };

  const handleDeleteFaq = async () => {
    if (!faqToDelete) return;

    try {
      const response = await deleteWithToken(`Questions_API/${faqToDelete}`, apiTokenData);
      if (response.status === 200) {
        toast.success(response.message);
        fetchFAQList(apiTokenData);
      } else {
        toast.error(response?.message || 'Failed to delete FAQ.');
      }
    } catch (error) {
      toast.error('Something went wrong while deleting.');
    } finally {
      setDeleteFAQ(false);
      setFaqToDelete(null);
    }
  };

  const handleDeleteAllFAQs = async () => {
    try {
      const response = await deleteWithToken('Questions_ALL_API/', apiTokenData);

      if (response.status === 200) {
        toast.success(response.message);
        setDeleteAllFAQ(false);
        fetchFAQList(apiTokenData);
      } else if (response.status === 400) {
        toast.error(response.message || 'Bad Request');
      } else {
        toast.error(response.message || 'Unexpected response from server');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <div className='comniq-container'>
      {/* <div className='knowledge-hub p-4'>
        <div className='knowledge-hub__header'>
          <h5 className='knowledge-hub__title outfit-bold'>FAQs</h5>
          <p className='knowledge-hub__subtitle text-muted mb-0 outfit-regular'>Generate and manage frequently asked questions</p>
        </div>
      </div> */}

      <div className='knowledge-hub p-4'>
        <div className='knowledge-hub__header'>
          <h5 className='knowledge-hub__title outfit-bold'>FAQ Management</h5>
          <p className='knowledge-hub__subtitle text-muted mb-0 outfit-regular'>Create and edit FAQs for your chatbot</p>
        </div>
        <div className='mt-2 mt-md-5'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4'>
            <div>
              <h5 className='knowledge-hub__title outfit-bold mb-2'>Frequently Asked Questions</h5>
              <div className='d-flex gap-2'>
                <span className='badge faq-badge bg-secondary rounded-pill outfit-regular'>{countList?.total_count} Total</span>
                <span className='badge faq-badge rounded-pill outfit-regular' style={{ backgroundColor: '#3b82f6' }}>
                  {countList?.source_2_count} Generated
                </span>
                <span className='badge faq-badge rounded-pill outfit-regular' style={{ backgroundColor: '#a855f7' }}>
                  {countList?.source_1_count} Added
                </span>
                <span className='badge faq-badge rounded-pill outfit-regular' style={{ backgroundColor: '#22c55e' }}>
                  {countList?.source_3_count} Edited
                </span>
              </div>
            </div>

            <div className='d-flex gap-2 flex-wrap justify-content-md-end'>
              <button className='btn-delete outfit-medium' onClick={() => setDeleteAllFAQ(true)}>
                Delete All FAQs
              </button>
              <button className='btn-add-faq outfit-medium' onClick={() => setAddFaqInput(!addFaqInput)}>
                <img src={PLUS_ICON} alt='plus-icon' className='me-2' /> Add FAQ
              </button>
              {/* <button className='btn-generate-faqs outfit-medium'>
                <img src={STAR_ICON} alt='plus-icon' className='me-2' /> Generate FAQs
              </button> */}
            </div>
          </div>
          {addFaqInput && (
            <div className='knowledge-hub mb-4'>
              <div className=''>
                <div className='mb-3'>
                  <div className='chatbox-input-text input-GPT new'>
                    <label className='outfit-medium'>Question:</label>
                    <div className='chatbox-input-field'>
                      <input
                        type='text'
                        placeholder='New Question'
                        className='form-control chatbox-input box-shadow-hover outfit-regular'
                        name='questionText'
                        value={faqFormData.questionText}
                        onChange={handleFaqInputChange}
                      />
                    </div>
                  </div>
                </div>
                <div className='mb-3'>
                  <div className='chatbox-input-text input-GPT new'>
                    <div className='d-flex justify-content-between align-items-center'>
                      <label className='outfit-medium'>Answer:</label>
                    </div>
                    <div className='chatbox-input-field'>
                      <textarea
                        rows='10'
                        className='form-control chatbox-input faq-textarea-height box-shadow-hover outfit-regular'
                        placeholder='New Answer'
                        name='answerText'
                        value={faqFormData.answerText}
                        onChange={handleFaqInputChange}
                        maxLength={1500}
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div className='d-flex flex-wrap justify-content-end gap-2 action-btn-group'>
                  <button className='btn-modal-cancel outfit-medium' onClick={() => setAddFaqInput(false)}>
                    ✖ Cancel
                  </button>
                  <button className='btn-modal-save outfit-medium' onClick={handleSaveFaq} disabled={!faqFormData.questionText || !faqFormData.answerText}>
                    ✔ Save
                  </button>
                </div>
              </div>
            </div>
          )}
          {loader ? (
            <div className='d-flex justify-content-center align-items-center p-3'>
              <div className='triple-bounce'>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
          ) : faqList.length > 0 ? (
            faqList.map((faq, idx) => (
              <div className={`knowledge-hub shadow-sm position-relative p-3 ${idx !== faqList.length - 1 ? 'mb-4' : ''}`}>
                <div key={faq.id}>
                  {isEditing === idx ? (
                    <>
                      <div className='mb-3'>
                        <div className='chatbox-input-text input-GPT new'>
                          <label className='outfit-medium'>Question:</label>
                          <div className='chatbox-input-field'>
                            <input
                              type='text'
                              className='form-control chatbox-input box-shadow-hover outfit-regular'
                              value={faq.question}
                              onChange={(e) => {
                                const updated = [...faqList];
                                updated[idx].question = e.target.value;
                                setFaqList(updated);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='mb-3'>
                        <div className='chatbox-input-text input-GPT new'>
                          <div className='d-flex justify-content-between align-items-center'>
                            <label className='outfit-medium'>Answer:</label>
                          </div>
                          <div className='chatbox-input-field'>
                            <textarea
                              rows='4'
                              className='form-control chatbox-input faq-textarea-height box-shadow-hover outfit-regular'
                              value={faq.answer}
                              onChange={(e) => {
                                const updated = [...faqList];
                                updated[idx].answer = e.target.value;
                                setFaqList(updated);
                              }}
                            ></textarea>
                          </div>
                        </div>
                      </div>
                      <div className='d-flex flex-wrap justify-content-end gap-2 action-btn-group'>
                        <button className='btn-modal-cancel outfit-medium' onClick={() => setIsEditing(false)}>
                          ✖ Cancel
                        </button>
                        <button className='btn-modal-save outfit-medium' onClick={() => handleEditSaveFaq(idx)}>
                          ✔ Save
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className='d-flex justify-content-between align-items-center mb-2'>
                        <p className='outfit-semibold mb-1'>{faq.question}</p>
                        <div className='d-flex align-items-center gap-2'>
                          <span
                            className={`badge faq-badge text-white outfit-regular rounded-pill`}
                            style={{
                              backgroundColor: faq?.source === '1' ? '#a855f7' : faq?.source === '2' ? '#3b82f6' : faq?.source === '3' ? '#22c55e' : '#6c757d',
                            }}
                          >
                            {faq?.source === '1' ? 'Added' : faq?.source === '2' ? 'ComniqAI Generated' : faq?.source === '3' ? 'Live Edit' : 'Unknown'}
                          </span>
                          <div className='faq-dropdown'>
                            <button
                              className='btn d-flex align-items-center faq-dropdown-toggle'
                              type='button'
                              id={`faqDropdown${idx}`}
                              data-bs-toggle='dropdown'
                              aria-expanded='false'
                              disabled={isProgressActive}
                            >
                              <BsThreeDots />
                            </button>

                            <ul className='dropdown-menu dropdown-menu-end faq-dropdown-menu' aria-labelledby={`faqDropdown${idx}`}>
                              <li>
                                <button className='dropdown-item d-flex align-items-center gap-2 outfit-regular' onClick={() => setIsEditing(idx)}>
                                  <MdOutlineModeEdit />
                                  Edit
                                </button>
                              </li>
                              <li>
                                <button
                                  className='dropdown-item text-danger d-flex align-items-center gap-2 outfit-regular'
                                  onClick={() => {
                                    setDeleteFAQ(true);
                                    setFaqToDelete(faq.id);
                                  }}
                                >
                                  <RiDeleteBinLine />
                                  Delete
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      <p className='outfit-regular mb-0'>{faq.answer}</p>
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            !addFaqInput && (
              <div className='knowledge-hub shadow-sm text-center p-3'>
                <p className='outfit-medium mb-0'>No FAQs available. Click "Add FAQ" to create one.</p>
              </div>
            )
          )}
          {deleteFAQ && (
            <div className='custom-popup'>
              <div className='popup-content'>
                <div className={'popup-head mt-3 mb-4'}>
                  <h4 className='outfit-medium'>Are you sure you want to delete?</h4>
                </div>
                <div className='popup-footer d-flex align-items-center gap-2'>
                  <button
                    className='btn-modal-save outfit-medium'
                    style={{ background: 'rgb(220, 53, 69)', borderColor: 'rgb(220, 53, 69)' }}
                    onClick={() => handleDeleteFaq(faqToDelete)}
                  >
                    Delete
                  </button>
                  <button className='btn-modal-cancel outfit-medium' data-dismiss='modal' onClick={() => setDeleteFAQ(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {deleteAllFAQ && (
            <CustomPopUp
              title='Are you sure you want to delete all FAQs?'
              onConfirm={handleDeleteAllFAQs}
              onClose={() => setDeleteAllFAQ(false)}
              confirmText='Delete'
              closeText='Cancel'
              variant='error'
              initialWidth='500px'
              loader={false}
            >
              <p className='outfit-regular'>This will permanently delete all your FAQ entries. Once deleted, you won’t be able to access or recover them.</p>
            </CustomPopUp>
          )}
        </div>
      </div>
    </div>
  );
};

export default FAQManager;
